import { Check, Sparkles, Users } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const FREE = ["Direct messaging", "Group messaging", "Photos", "Documents", "Voice messages", "Replies", "Reactions", "Search"];

const PRO = [
  "Everything in Messaging", "Create Projects", "Project Dashboard", "Tasks & Deadlines",
  "Approvals", "Measurements", "Project Photos", "Project Documents", "Addresses",
  "Contacts", "Timeline", "Project Summary", "20 GB storage",
];

const STORAGE = [
  ["50 GB", "+£5/mo"],
  ["100 GB", "+£10/mo"],
  ["250 GB", "+£20/mo"],
  ["500 GB", "+£40/mo"],
  ["1 TB", "+£75/mo"],
];

const startCheckout = (lookupKey) => async (e) => {
  const btn = e.currentTarget;
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Redirecting to secure checkout…";
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
    });
    const data = await res.json();
    if (!res.ok || !data.checkout_url) throw new Error();
    window.location.href = data.checkout_url;
  } catch {
    btn.disabled = false;
    btn.textContent = original;
  }
};

export const Pricing = () => (
  <section id="pricing" className="bg-white py-28 sm:py-36" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="11" label="Pricing" />
      <div className="mt-8 max-w-3xl">
        <Reveal delay={0.1}>
          <SectionHeading>Simple. Like everything else.</SectionHeading>
        </Reveal>
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-6 items-stretch">
        <Reveal>
          <div className="h-full rounded-3xl bg-white border border-diem-line p-8 sm:p-10 flex flex-col shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="pricing-free-card">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-diem-muted">Messaging</p>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-display text-6xl font-medium tracking-tighter text-diem-ink">Free</span>
            </div>
            <p className="mt-3 text-sm text-diem-muted">Forever. For everyone.</p>
            <ul className="mt-8 space-y-3 flex-1">
              {FREE.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[15px] text-diem-slate">
                  <Check className="w-4 h-4 text-diem-ok shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-free-cta"
              onClick={() => scrollToId("#waitlist")}
              className="mt-10 w-full rounded-full border border-diem-line bg-white py-4 font-semibold text-diem-ink hover:bg-diem-paper hover:border-diem-blue/40 active:scale-[0.98] transition-[background-color,border-color,transform] duration-300"
            >
              Join the waitlist
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative h-full rounded-3xl bg-diem-ink p-8 sm:p-10 flex flex-col text-white shadow-[0_40px_90px_-20px_rgba(15,23,42,0.5)] overflow-hidden" data-testid="pricing-owner-card">
            <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(500px 260px at 90% 0%, #0055FF, transparent 65%)" }} />
            <div className="relative">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-diem-sky">Project Owner</p>
                <span className="rounded-full bg-diem-ok/15 text-diem-ok text-[11px] font-bold px-3 py-1">30 days free</span>
                <span className="rounded-full bg-diem-blue/20 text-diem-sky text-[11px] font-bold px-3 py-1 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Founding Member
                </span>
              </div>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-6xl font-medium tracking-tighter">£10</span>
                <span className="text-white/50 font-semibold mb-2">/month</span>
              </div>
              <p className="mt-3 text-sm text-white/50">One subscription powers every Project you own.</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 flex-1">
                {PRO.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-[14px] text-white/80">
                    <Check className="w-4 h-4 text-diem-sky shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-10 space-y-3">
                <button
                  data-testid="pricing-owner-cta"
                  onClick={startCheckout("project_owner_monthly")}
                  className="w-full rounded-full bg-diem-blue py-4 font-semibold text-white shadow-[0_12px_36px_rgba(0,85,255,0.5)] hover:shadow-[0_18px_48px_rgba(0,85,255,0.6)] hover:-translate-y-0.5 active:scale-[0.98] transition-[box-shadow,transform] duration-300 disabled:opacity-70"
                >
                  Start 30 days free · £10/month
                </button>
                <button
                  data-testid="pricing-owner-yearly-cta"
                  onClick={startCheckout("project_owner_yearly")}
                  className="w-full rounded-full border border-white/25 py-4 font-semibold text-white/85 hover:bg-white/5 hover:border-diem-sky/50 active:scale-[0.98] transition-[background-color,border-color,transform] duration-300 disabled:opacity-70"
                >
                  Go yearly · £100/year <span className="text-diem-ok font-bold">— two months free</span>
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-6 rounded-3xl border border-diem-blue/25 bg-diem-blue/[0.04] px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="guests-never-pay-banner">
          <span className="w-11 h-11 rounded-2xl bg-diem-blue flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="font-display text-xl font-semibold tracking-tight text-diem-ink">Guests never pay.</p>
            <p className="mt-1 text-sm text-diem-slate leading-relaxed">
              Only the person who owns the Project needs a Project Owner subscription. Everyone invited
              to participate can use Deyem without paying for that Project.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-12" data-testid="storage-upgrades">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-diem-muted mb-4">
            Need more room? 20 GB included — upgrade any Project:
          </p>
          <div className="flex flex-wrap gap-2.5">
            {STORAGE.map(([size, price]) => (
              <span key={size} className="rounded-full border border-diem-line bg-white px-4 py-2 text-sm font-semibold text-diem-slate hover:border-diem-blue/40 hover:text-diem-blue transition-colors duration-300 cursor-default">
                {size} <span className="text-diem-muted font-medium">· {price}</span>
              </span>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
